import React, { useCallback, useRef } from "react";
import Select from "react-dropdown-select";
import styled from "@emotion/styled";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  THEME_COLORS,
  FONT_SIZE,
  OTHER_CONSTANTS,
  FONT_FAMILY,
} from "../../themes";
import { Image } from "react-native-elements";
import { TouchableOpacity, View, ScrollView } from "react-native";
import Text from "../CustomTextComponent";
import { toCamelCase, getDeepSearchedItems } from "../../../common/helper";

export type SelectInputVarsType = {
  labelField: string;
  valueField: string;
  searchBy: string;
  fieldName: string;
  options: object[];
};
type ICustomSelectProps = {
  multi?: boolean;
  level?: string;
  hideSelectField?: string;
  isRequired?: boolean;
  disabled?: boolean;
  values: object[];
  isLabelVisible?: boolean;
  filterObj?: any;
  filterHandler?: (data, search, obj) => any;
  type?: "select" | "filter";
  onChange: (value) => void;
  onSearch?: (searchInput: string) => void;
  closeOnSelect?: boolean;
  isClearVisible?: boolean;
  width?: number;
  height?: number;
  backgroundColor?: string;
  virtualized?: boolean;
  rowHeight?: number;
  header?: any;
  renderItem?: (data: {
    option: object;
    isCheckboxVisible: boolean;
  }) => JSX.Element;
  keyField?: string;
} & SelectInputVarsType;

export const CustomSelectTwo = (props: ICustomSelectProps) => {
  const isLabelVisible = !!props.isLabelVisible ?? false;
  const type = !!props.type ? props.type : "select";
  const isHideSelectInProps = !!props.hideSelectField;
  const hideSelectField = props.hideSelectField;
  const isClearVisible =
    type === "filter" ||
    (!!props.isClearVisible ? props.isClearVisible : false);
  const virtualized = !!props.virtualized ? props.virtualized : false;
  const header = !!props.header ? props.header : null;
  const filterObj = props.filterObj;

  const level = props.level;
  const filterHandler = props.filterHandler;
  const onSearch = props.onSearch;
  const key = !!props.keyField ? props.keyField : props.valueField;

  const selectStyles =
    type === "filter"
      ? { border: 0 }
      : isLabelVisible
        ? {
            minHeight: "auto",
            paddingVertical: 5,
            border: "none",
          }
        : {
            minHeight: "auto",
            boxShadow: `0 0 10px 0 ${THEME_COLORS.shadow1}`,
            padding: 0,
            marginTop: 5,
            marginBottom: 5,
            backgroundColor: THEME_COLORS.white,
            borderRadius: 10,
            borderColor:
              props.isRequired && !(props.values.length > 0)
                ? THEME_COLORS.red
                : THEME_COLORS.black2,
          };
  const renderItem = (option: object, isCheckboxVisible) => {
    return !!props.renderItem ? (
      props.renderItem({ option, isCheckboxVisible })
    ) : (
      <ItemLabel
        style={{
          fontFamily: isCheckboxVisible ? "FuturaPTBook" : "FuturaPTMedium",
        }}
      >
        {option[props.labelField]}
      </ItemLabel>
    );
  };

  const onSearchFn = ({ props, state, methods }) => {
    if (!!!onSearch) {
      let filteredData = getDeepSearchedItems(
        props.options,
        state.search,
        props.searchBy || props.labelField
      );
      filteredData = !!filterHandler
        ? filterHandler(filteredData, state.search, filterObj)
        : filteredData;
      return filteredData;
    } else {
    }
  };
  const renderItemList = ({ filteredData, methods, state, props }) => {
    return (
      <Items>
        {filteredData.map((option, index) => {
          if (!props.keepSelectedInList && methods.isSelected(option)) {
            return null;
          }

          const isCursorOnThisOption = state.cursor === index;
          const isOptionSelected =
            props.values.length == 0
              ? false
              : state.values
                  .map((item) => item[props.valueField])
                  .indexOf(option[props.valueField]) !== -1;
          const isCheckboxVisible = isHideSelectInProps
            ? option[hideSelectField]
            : true;
          return (
            <Item
              disabled={option.disabled}
              key={option[key]}
              ref={(r) => {
                r && isCursorOnThisOption
                  ? r.scrollIntoView({ block: "center", behavior: "smooth" })
                  : null;
                if (r)
                  r.onclick = () => {
                    option.disabled || !isCheckboxVisible
                      ? null
                      : methods.addItem(option);
                  };
              }}
              style={{
                backgroundColor: isCursorOnThisOption
                  ? THEME_COLORS.primary
                  : null,
                borderRadius: 4,
                alignItems: "center",
                cursor: isCheckboxVisible ? "pointer" : null,
                maxWidth: "200px",
                color: isCursorOnThisOption ? THEME_COLORS.white : null,
                marginLeft: option[level] ? option[level] * 10 : 0,
              }}
            >
              {isCheckboxVisible ? (
                <StyledCheckboxInput
                  readOnly={true}
                  type="checkbox"
                  checked={isOptionSelected}
                />
              ) : null}
              {renderItem(option, isCheckboxVisible)}
            </Item>
          );
        })}
      </Items>
    );
  };

  const renderItemListVirtualized = ({
    filteredData,
    methods,
    state,
    props,
  }) => {
    return (
      <VirtualizedDropdownListWithHeader
        filteredData={filteredData}
        methods={methods}
        state={state}
        props={props}
        renderItem={renderItem}
        hideSelectField={hideSelectField}
        isHideSelectInProps={isHideSelectInProps}
        level={level}
      />
    );
  };

  const renderList = virtualized ? renderItemListVirtualized : renderItemList;

  const customDropdownRenderer = ({ props, state, methods }) => {
    const filteredData = methods.searchResults();

    return (
      <DropDown>
        <SearchAndToggle color={props.color}>
          <Buttons>
            {props.multi && (
              <Button
                className="select-all"
                style={{ justifyContent: "flex-end" }}
                ref={(r) => {
                  if (r)
                    r.onclick = () => {
                      let oldVals = state.values.map(
                        (item) => item[props.valueField]
                      );
                      let filteredNewData = filteredData.filter(
                        (item) => !oldVals.includes(item[props.valueField])
                      );
                      methods.selectAll([...state.values, ...filteredNewData]);
                    };
                }}
              >
                Select All
              </Button>
            )}
            {props.values.length > 0 && isClearVisible ? (
              <Button
                className="clear"
                style={{ justifyContent: "flex-end" }}
                ref={(r) => {
                  if (r)
                    r.onclick = () => {
                      methods.clearAll();
                    };
                }}
                onClick={methods.clearAll}
              >
                Clear
              </Button>
            ) : (
              <></>
            )}
          </Buttons>
          <input
            style={{ border: "none", backgroundColor: THEME_COLORS.grey2 }}
            autoFocus={true}
            type="text"
            value={state.search}
            onChange={(v) => {
              console.log(v.target.value);
              methods.setSearch(v);
            }}
            placeholder={"Search " + props.fieldName}
          />
        </SearchAndToggle>
        {header}
        {renderList({ filteredData, props, methods, state })}
      </DropDown>
    );
  };

  const className = "css-1ujv2ml-ReactDropdownSelect";
  let element = document.getElementById(className);
  if (!!!element) {
    element = document.createElement("div");
    element.setAttribute("id", className);
    element.setAttribute("class", className);
    element.setAttribute(
      "style",
      "width: 0%; padding: 0px; z-index: 99999 !important"
    );
    document.body.appendChild(element);
  }
  let closeOnSelect = true;
  if (!!props.multi && props.multi) {
    closeOnSelect = false;
  } else if (!!props.closeOnSelect) {
    closeOnSelect = props.closeOnSelect;
  }

  const renderStyledSelect = () => (
    <StyledSelect
      labelField={props.labelField}
      valueField={props.valueField}
      searchBy={props.searchBy}
      dropdownPosition="auto"
      values={props.values}
      options={props.options}
      rowHeight={props.rowHeight}
      onChange={props.onChange}
      multi={props.multi}
      closeOnSelect={closeOnSelect}
      disabled={props.disabled}
      dropdownHeight={"400px"}
      dropdownHandle={type !== "filter"}
      color={THEME_COLORS.primary}
      style={selectStyles}
      searchFn={onSearchFn}
      clearOnSelect={false}
      fieldName={props.fieldName}
      contentRenderer={({ props, state }) => {
        const values = props.values;
        const valuesLength = values.length;
        const isValueSelected = valuesLength > 0;
        let showValues = values;
        const maxVisibleValues = 2;
        if (isValueSelected && valuesLength > maxVisibleValues) {
          showValues = values.slice(0, maxVisibleValues);
        }
        const extraLength = valuesLength - maxVisibleValues;
        const showText = isValueSelected
          ? `${showValues.map((item) => item[props.labelField]).join(", ")}`
          : "Select " + props.fieldName;
        return type === "filter" ? (
          isValueSelected ? (
            <Image
              source={require("../../../assets/filterIcon.ico")}
              style={{ width: 15, height: 12 }}
            />
          ) : (
            <Image
              source={require("../../../assets/filter_icon.png")}
              style={{ width: 15, height: 12 }}
            />
          )
        ) : (
          <>
            <Text
              numberOfLines={2}
              fontSize={"medium"}
              type_of_six={"three_six"}
            >
              {showText}
            </Text>
            {extraLength > 0 ? (
              <Text
                fontSize={"small"}
                type_of_six={"two_six"}
                style={{ color: THEME_COLORS.success }}
              >
                {" "}
                + {extraLength} more
              </Text>
            ) : null}
          </>
        );
      }}
      portal={typeof document !== `undefined` && element}
      dropdownRenderer={customDropdownRenderer}
    />
  );

  return !isLabelVisible ? (
    renderStyledSelect()
  ) : (
    <View
      style={{
        width: !!props.width ? props.width : 250,
        backgroundColor: !!props.backgroundColor
          ? props.backgroundColor
          : THEME_COLORS.grey2,
        maxHeight: 80,
        paddingHorizontal: 10,
        borderRadius: 8,
        marginLeft: 10,
        marginVertical: 10,
      }}
    >
      <Text style={{ marginVertical: 5 }} fontSize={"small"}>
        {props.label ?? ""}
      </Text>
      {renderStyledSelect()}
    </View>
  );
};

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const StyledSelect = styled(Select)`
  .react-dropdown-select-dropdown {
    // position: absolute;
    // left: 0;
    // border: none;
    width: fit-content !important;
    // padding: 0;
    // display: flex;
    // flex-direction: column;
    // border-radius: 2px;
    // overflow: auto;
    z-index: 9999999 !important;
    // background: #333;
    //color: #fff !important;
  }
  .react-dropdown-select-content {
    font-size: ${FONT_SIZE.s2};
    padding-left: 5px;
    align-content: center;
    min-height: ${OTHER_CONSTANTS.selectInputHeight};
    font-family: ${FONT_FAMILY.primary};
  }
`;

const SearchAndToggle = styled.div`
  display: flex;
  flex-direction: column;
  input {
    margin: 10px 10px 0;
    line-height: 30px;
    padding: 0px 20px;
    border: 1px solid #ccc;
    border-radius: 3px;
    :focus {
      outline: none;
      border: 1px solid ${THEME_COLORS.primary};
    }
  }
`;

const Items = styled.div`
  overflow: auto;
  min-height: 10px;
  max-height: 200px;
`;

const TouchableOpacityG = styled(TouchableOpacity)`
  :hover {
    ${({ disabled }) => !disabled && "background: #f2f2f2; "}
  }
  // ${({ disabled }) => disabled && "text-decoration: line-through;"}
`;

const Item = styled.div`
  display: flex;
  margin: 5px;
  align-items: baseline;
  font-family: ${FONT_FAMILY.primary};
  font-size: ${FONT_SIZE.s2};
  :hover {
    ${({ disabled }) => !disabled && "background: #f2f2f2; "}
  }
  // ${({ disabled }) => disabled && "text-decoration: line-through;"}
`;

const StyledCheckboxInput = styled.input``;

const ItemLabel = styled.div`
  margin: 5px 10px;
`;

const VirtualizedListContainer = styled.div`
  overflow: auto;
  min-height: 10px;
  max-height: 150px;
  height: 150px;
  width: 300px;
`;

const VirtualizedListInner = styled.div`
  position: relative;
  width: 100%;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  & div {
    margin: 10px 0 0 10px;
    font-weight: 600;
  }
`;

const Button = styled.button`
  background: none;
  border: 1px solid #555;
  color: #555;
  border-radius: 3px;
  margin: 10px 10px 0;
  padding: 3px 5px;
  font-size: 10px;
  text-transform: uppercase;
  cursor: pointer;
  outline: none;
  &.clear {
    color: tomato;
    border: 1px solid tomato;
  }
  &.select-all {
    color: green;
    border: 1px solid green;
  }
  :hover {
    border: 1px solid deepskyblue;
    color: deepskyblue;
  }
`;

const DropDown = styled.div`
  .css-1snwlti-DropDown {
    min-width: 100;
  }
`;

function VirtualizedDropdownListWithHeader({
  filteredData,
  methods,
  state,
  props: listProps,
  renderItem,
  hideSelectField,
  isHideSelectInProps,
  level,
}: {
  filteredData: object[];
  methods: any;
  state: any;
  props: any;
  renderItem: (option: object, isCheckboxVisible: boolean) => React.ReactNode;
  hideSelectField?: string;
  isHideSelectInProps: boolean;
  level?: string;
}) {
  const rowHeight = listProps.rowHeight || 36;
  const listRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredData.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
  });
  return (
    <DropDown>
      <VirtualizedListContainer ref={listRef}>
        <VirtualizedListInner style={{ height: rowVirtualizer.getTotalSize() }}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const option = filteredData?.[virtualRow.index] as any;
            if (!option) return null;
            if (!listProps.keepSelectedInList && methods.isSelected(option)) {
              return null;
            }
            const isCursorOnThisOption = state.cursor === virtualRow.index;
            const isOptionSelected =
              listProps.values?.length === 0
                ? false
                : state.values
                    .map((item: any) => item[listProps.valueField])
                    .indexOf(option[listProps.valueField]) !== -1;
            const isCheckboxVisible = isHideSelectInProps ? option[hideSelectField] : true;
            return (
              <TouchableOpacityG
                key={virtualRow.key}
                disabled={option.disabled}
                ref={(r: any) => {
                  if (r) {
                    isCursorOnThisOption ? r.scrollIntoView({ block: "center", behavior: "smooth" }) : null;
                    (r as HTMLElement).onclick = () => {
                      if (!(option.disabled || !isCheckboxVisible)) {
                        methods.addItem(option);
                      }
                    };
                  }
                }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                  backgroundColor: isCursorOnThisOption ? THEME_COLORS.primary : undefined,
                  borderRadius: 4,
                  alignItems: "center",
                  flexDirection: "row",
                  cursor: isCheckboxVisible ? "pointer" : undefined,
                  color: isCursorOnThisOption ? THEME_COLORS.white : undefined,
                  marginLeft: option[level] ? option[level] * 10 : 0,
                }}
              >
                {isCheckboxVisible ? (
                  <StyledCheckboxInput
                    type="checkbox"
                    readOnly={true}
                    onChange={undefined}
                    checked={isOptionSelected}
                  />
                ) : null}
                {renderItem(option, isCheckboxVisible)}
              </TouchableOpacityG>
            );
          })}
        </VirtualizedListInner>
      </VirtualizedListContainer>
    </DropDown>
  );
}
